<template>
    <div class="mb-5 mt-8 flex justify-between space-x-10">
        <div class="shrink">
            <h2 class="font-semibold text-slate-800">Creator Background</h2>
            <p class="text-sm">
                This is more information about the creator's motivations, personal life, and business ventures.
            </p>
        </div>
    </div>

    <div v-if="isLoading" class="mt-6 space-y-4">
        <SkeletonCreatorProfileBackground />
    </div>

    <div v-else-if="biography?.features && hasExtendedBiographyData" class="mt-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <!-- Personal & Family -->
            <div v-if="hasPersonalInformation" class="space-y-4">
                <h4 class="text-md border-b border-slate-200 pb-2 font-medium text-slate-700">Personal Information</h4>

                <!-- Real Name -->
                <div v-if="biography.features.real_first_name || biography.features.real_last_name" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Legal Name</h5>
                    <div class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        {{ biography.features.real_first_name }} {{ biography.features.real_last_name }}
                    </div>
                </div>

                <!-- Aliases -->
                <div v-if="biography.features.aliases?.length" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Known As</h5>
                    <div class="flex flex-wrap gap-1">
                        <span
                            v-for="alias in biography.features.aliases"
                            :key="alias"
                            class="inline-flex items-center rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
                        >
                            {{ alias }}
                        </span>
                    </div>
                </div>

                <!-- Basic Demographics -->
                <div v-if="hasBasicDemographics" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Demographics</h5>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div
                            v-if="biography.features.gender"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="font-medium">Gender:</span> {{ formatGender(biography.features.gender) }}
                        </div>
                        <div
                            v-if="biography.features.ethnicity"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="font-medium">Ethnicity:</span> {{ biography.features.ethnicity }}
                        </div>
                        <div
                            v-if="biography.features.nationality"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="font-medium">Nationality:</span> {{ biography.features.nationality }}
                        </div>
                    </div>
                </div>

                <!-- Location Information -->
                <div v-if="hasLocationInfo" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Location</h5>
                    <div class="space-y-1 rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <div v-if="biography.features.residence?.city || biography.features.residence?.state">
                            <span class="font-medium">Current Residence:</span>
                            {{ formatLocation(biography.features.residence) }}
                        </div>
                        <div v-if="biography.features.residence?.country_code">
                            <span class="font-medium">Country:</span>
                            {{ getCountryFromCode(biography.features.residence.country_code) }}
                        </div>
                    </div>
                </div>

                <!-- Language -->
                <div v-if="biography.features.primary_language" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Primary Language</h5>
                    <div class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        {{ getLanguageFromCode(biography.features.primary_language) }}
                    </div>
                </div>

                <!-- Birthday -->
                <div v-if="biography.features.birthday" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Birthday</h5>
                    <div class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        {{ formatBirthday(biography.features.birthday) }}
                    </div>
                </div>

                <!-- Camera Presence -->
                <div v-if="biography.features.appearance" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Camera Presence</h5>
                    <div class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <div class="flex items-center space-x-2">
                            <AppIcon
                                :name="getCameraPresenceIcon(biography.features.appearance)"
                                class="h-4 w-4 shrink-0"
                            />
                            <span>{{ getCameraPresenceText(biography.features.appearance) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Height -->
                <!-- <div v-if="biography.features.height_in_inches" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Height</h5>
                    <div class="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">
                        {{ formatHeight(biography.features.height_in_inches) }}
                    </div>
                </div> -->

                <!-- Family -->
                <div v-if="biography.features.family" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Family & Personal Life</h5>
                    <div class="space-y-1">
                        <div
                            v-if="biography.features.family.relationship_status"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="font-medium">Relationship:</span>
                            {{ formatRelationshipStatus(biography.features.family.relationship_status) }}
                        </div>
                        <div
                            v-if="biography.features.family.has_children !== null"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <span class="font-medium">Children:</span>
                            {{ biography.features.family.has_children ? 'Yes' : 'No' }}
                        </div>

                        <!-- Notable Family Members -->
                        <div v-if="biography.features.family.notable_family_members?.length" class="mt-2">
                            <div class="mb-1 text-xs font-medium text-slate-600">Notable Family Members</div>
                            <div class="space-y-1">
                                <div
                                    v-for="member in biography.features.family.notable_family_members"
                                    :key="member.name"
                                    class="rounded bg-slate-50 px-2 py-1 text-xs text-slate-600"
                                >
                                    <span class="font-medium">{{ member.name }}</span> ({{ member.relationship }})
                                    <span v-if="member.public_figure" class="ml-1 text-emerald-600"
                                        >• Public Figure</span
                                    >
                                    <span v-if="member.appears_in_content" class="ml-1 text-blue-600"
                                        >• Appears in Content</span
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Physical Characteristics -->
                <div v-if="biography.features.appearance?.notable_physical_characteristics?.length" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Notable Characteristics</h5>
                    <div class="space-y-1">
                        <div
                            v-for="characteristic in biography.features.appearance.notable_physical_characteristics"
                            :key="characteristic"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            {{ characteristic }}
                        </div>
                    </div>
                </div>

                <!-- AI Generated Influencer Notice -->
                <div v-if="biography.features.appearance?.is_ai_generated_influencer" class="space-y-2">
                    <div class="flex items-center space-x-2 rounded bg-purple-50 px-3 py-2 text-sm text-purple-700">
                        <AppIcon name="robot" class="h-4 w-4" />
                        <span class="font-medium">AI-Generated Virtual Influencer</span>
                    </div>
                </div>
            </div>

            <!-- Background & Career -->
            <div v-if="biography.features.background" class="space-y-4">
                <h4 class="text-md border-b border-slate-200 pb-2 font-medium text-slate-700">Background & Career</h4>

                <!-- Education -->
                <div v-if="biography.features.background.education?.length" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Education</h5>
                    <div class="space-y-1">
                        <div
                            v-for="edu in biography.features.background.education"
                            :key="edu.institution"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <div class="font-medium">{{ edu.institution }}</div>
                            <div v-if="edu.degree || edu.field" class="text-slate-600">
                                {{ edu.degree }}{{ edu.degree && edu.field ? ' in ' : '' }}{{ edu.field }}
                            </div>
                            <div v-if="edu.start_year || edu.end_year" class="text-xs text-slate-500">
                                {{ edu.start_year }}{{ edu.start_year && edu.end_year ? ' - ' : '' }}{{ edu.end_year }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Original Motivation -->
                <div v-if="biography.features.background.original_motivation" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Why They Started Creating</h5>
                    <p class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        {{ biography.features.background.original_motivation }}
                    </p>
                </div>

                <!-- Career -->
                <div v-if="biography.features.background.career?.length" class="space-y-2">
                    <h5 class="text-sm font-medium text-slate-600">Professional Experience</h5>
                    <div class="space-y-1">
                        <div
                            v-for="job in sortedCareer"
                            :key="job.company"
                            class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                        >
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ job.role }}</span>
                                <span
                                    v-if="job.current"
                                    class="inline-flex items-center rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                                >
                                    Current
                                </span>
                            </div>
                            <div class="text-slate-600">{{ job.company }} • {{ job.industry }}</div>
                            <div v-if="job.description" class="mt-1 text-xs text-slate-500">
                                {{ job.description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Business & Collaborations -->
            <div v-if="biography.features.business" class="col-span-1 space-y-4 md:col-span-2">
                <h4 class="text-md border-b border-slate-200 pb-2 font-medium text-slate-700">
                    Business & Collaborations
                </h4>

                <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Recent Collaborations - Takes 2/3 width -->
                    <div v-if="biography.features.business.collaborations?.length" class="space-y-2 md:col-span-2">
                        <h5 class="text-sm font-medium text-slate-600">Recent Collaborations</h5>
                        <div class="grid grid-cols-1 gap-2 xl:grid-cols-2">
                            <div
                                v-for="collab in visibleCollaborations"
                                :key="collab.collaboration_partner"
                                class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                            >
                                <div class="font-medium">{{ collab.collaboration_partner }}</div>
                                <div class="text-xs text-slate-600">{{ collab.collaboration_type }}</div>
                                <div v-if="collab.description" class="mt-1 text-xs text-slate-500">
                                    {{ collab.description }}
                                </div>
                            </div>
                        </div>

                        <!-- Expand/Collapse Button -->
                        <div v-if="hasMoreCollaborations" class="px-3 text-xs text-slate-500">
                            <button
                                class="text-blue-600 underline hover:text-blue-800 focus:outline-none"
                                @click="toggleSection('collaborations')"
                            >
                                <span v-if="!expandedSections.collaborations">
                                    +{{ biography.features.business.collaborations.length - 5 }} more collaborations
                                </span>
                                <span v-else> Show less </span>
                            </button>
                        </div>
                    </div>

                    <!-- Business Ventures - Takes 1/3 width -->
                    <div v-if="biography.features.business.ventures?.length" class="space-y-2 md:col-span-1">
                        <h5 class="text-sm font-medium text-slate-600">Business Ventures</h5>
                        <div class="space-y-1">
                            <div
                                v-for="venture in visibleVentures"
                                :key="venture.venture_name"
                                class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                            >
                                <div class="font-medium">{{ venture.venture_name }}</div>
                                <div class="text-xs text-slate-600">
                                    {{ venture.venture_type }}{{ venture.role ? ` • ${venture.role}` : '' }}
                                </div>
                                <div v-if="venture.launch_date" class="text-xs text-slate-500">
                                    Launched: {{ formatDate(venture.launch_date) }}
                                </div>
                                <div v-if="venture.description" class="mt-1 text-xs text-slate-500">
                                    {{ venture.description }}
                                </div>
                            </div>

                            <!-- Expand/Collapse Button -->
                            <div v-if="hasMoreVentures" class="px-3 text-xs text-slate-500">
                                <button
                                    class="text-blue-600 underline hover:text-blue-800 focus:outline-none"
                                    @click="toggleSection('ventures')"
                                >
                                    <span v-if="!expandedSections.ventures">
                                        +{{ biography.features.business.ventures.length - 3 }} more ventures
                                    </span>
                                    <span v-else> Show less </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Platform Presence -->
            <div v-if="biography.features.platforms?.length" class="col-span-1 space-y-4 md:col-span-2">
                <h4 class="text-md border-b border-slate-200 pb-2 font-medium text-slate-700">Platform Presence</h4>
                <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div
                        v-for="platform in biography.features.platforms"
                        :key="platform.platform_name"
                        class="rounded bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    >
                        <div class="font-medium">{{ platform.platform_name }}</div>
                        <div class="text-xs text-slate-600">@{{ platform.handle?.replace('@', '') }}</div>
                        <div v-if="platform.primary_content_type" class="text-xs text-slate-500">
                            {{ platform.primary_content_type }}
                        </div>
                        <a
                            v-if="platform.platform_url"
                            :href="platform.platform_url"
                            target="_blank"
                            class="mt-1 inline-block text-xs text-blue-600 hover:text-blue-800"
                        >
                            <AppIcon name="external-link-alt" class="mr-1 h-3 w-3" />
                            Visit
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="mt-6">
        <div class="rounded border border-slate-100 px-3 py-5">
            <div class="text-md flex">
                <div class="m-auto text-sm text-slate-600">
                    Background details for this creator are not currently available.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters } from 'vuex'
import SkeletonCreatorProfileBackground from '@/partials/skeletons/SkeletonCreatorProfileBackground.vue'

export default {
    name: 'CreatorBackground',

    components: {
        SkeletonCreatorProfileBackground,
    },

    props: {
        biography: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            expandedSections: {
                collaborations: false,
                ventures: false,
            },
        }
    },

    computed: {
        ...mapGetters([
            'getFromDictionary',
            'getLanguageFromCode',
            'getCountryFromCode',
            'getCodeFromCountry',
            'getCodeFromLanguage',
            'isSfStaff',
            'isDevModeEnabled',
            'checkPermission',
            'selfOperator',
        ]),

        sortedCareer() {
            if (!this.biography?.features?.background?.career) return []

            return _.orderBy(this.biography.features.background.career, ['current'], ['desc'])
        },

        hasExtendedBiographyData() {
            if (!this.biography?.features) return false

            return !!(
                this.biography.features.background ||
                this.hasPersonalInformation ||
                this.biography.features.business ||
                this.biography.features.platforms?.length
            )
        },

        hasPersonalInformation() {
            if (!this.biography?.features) return false

            return !!(
                this.biography.features.real_first_name ||
                this.biography.features.real_last_name ||
                this.biography.features.gender ||
                this.biography.features.age_range ||
                this.biography.features.ethnicity ||
                this.biography.features.nationality ||
                this.biography.features.residence ||
                this.biography.features.primary_language ||
                this.biography.features.birthday ||
                this.biography.features.height_in_inches ||
                this.biography.features.appearance ||
                this.biography.features.family ||
                this.biography.features.aliases?.length ||
                this.biography.features.appearance?.notable_physical_characteristics?.length ||
                this.biography.features.appearance?.is_ai_generated_influencer
            )
        },

        hasBasicDemographics() {
            return !!(
                this.biography?.features?.gender ||
                this.biography?.features?.age_range ||
                this.biography?.features?.ethnicity ||
                this.biography?.features?.nationality
            )
        },

        hasLocationInfo() {
            const residence = this.biography?.features?.residence
            return !!(residence?.city || residence?.state || residence?.country_code)
        },

        visibleCollaborations() {
            const collaborations = this.biography?.features?.business?.collaborations || []
            if (this.expandedSections.collaborations || collaborations.length <= 5) {
                return collaborations
            }
            return collaborations.slice(0, 5)
        },

        visibleVentures() {
            const ventures = this.biography?.features?.business?.ventures || []
            if (this.expandedSections.ventures || ventures.length <= 3) {
                return ventures
            }
            return ventures.slice(0, 3)
        },

        hasMoreCollaborations() {
            const collaborations = this.biography?.features?.business?.collaborations || []
            return collaborations.length > 5
        },

        hasMoreVentures() {
            const ventures = this.biography?.features?.business?.ventures || []
            return ventures.length > 3
        },
    },

    methods: {
        formatRelationshipStatus(status) {
            const statusMap = {
                single: 'Single',
                dating: 'Dating',
                married: 'Married',
                engaged: 'Engaged',
                divorced: 'Divorced',
                widowed: 'Widowed',
                separated: 'Separated',
                'in a relationship': 'In a Relationship',
                'domestic partnership': 'Domestic Partnership',
                'it\'s complicated': 'It\'s Complicated',
            }

            return statusMap[status] || status
        },

        formatDate(dateString) {
            if (!dateString) return ''
            return moment(dateString).format('MMM YYYY')
        },

        formatBirthday(birthday) {
            if (!birthday) return ''

            const parts = []

            if (birthday.birthday_month) {
                parts.push(
                    moment()
                        .month(birthday.birthday_month - 1)
                        .format('MMMM'),
                )
            }

            if (birthday.birthday_day) {
                parts.push(moment().date(birthday.birthday_day).format('Do'))
            }

            if (birthday.birthday_year) {
                parts.push(birthday.birthday_year)
            }

            return parts.join(' ')
        },

        formatHeight(heightInInches) {
            if (!heightInInches || heightInInches <= 0) return ''

            const feet = Math.floor(heightInInches / 12)
            const inches = Math.round(heightInInches) % 12

            return `${feet}'${inches}"`
        },

        formatGender(gender) {
            const genderMap = {
                male: 'Male',
                female: 'Female',
                'non-binary': 'Non-Binary',
                other: 'Other',
            }

            return genderMap[gender] || gender
        },

        formatLocation(residence) {
            if (!residence) return ''

            const parts = []

            if (residence.city) {
                parts.push(residence.city)
            }

            if (residence.state) {
                parts.push(residence.state)
            }

            return parts.join(', ')
        },

        getCameraPresenceIcon(appearance) {
            if (appearance?.is_ai_generated_influencer) {
                return 'robot'
            } else if (appearance?.is_vtuber) {
                return 'user-astronaut'
            } else if (appearance?.shows_face === true) {
                return 'camera'
            } else if (appearance?.shows_face === false) {
                return 'user-secret'
            }
            return 'question'
        },

        getCameraPresenceText(appearance) {
            if (appearance?.is_ai_generated_influencer) {
                return 'AI-Generated Virtual Influencer'
            } else if (appearance?.is_vtuber) {
                return 'Virtual Avatar (V-Tuber)'
            } else if (appearance?.shows_face === true) {
                return 'Shows Face on Camera'
            } else if (appearance?.shows_face === false) {
                return 'Faceless Creator'
            }
            return 'Unknown'
        },

        toggleSection(sectionName) {
            this.expandedSections[sectionName] = !this.expandedSections[sectionName]
        },
    },
}
</script>
